import { useEffect, useRef } from 'react';
import { FileText } from 'lucide-react';
import type { Source } from '@/data/fixtures';
import { useSourceArrayBuffer } from '@/components/features/learning/sources/useSourceText';
import {
  ViewerLoading,
  ViewerError,
} from '@/components/features/learning/sources/viewers/shell/ViewerStates';
import { ViewerShell } from '@/components/features/learning/sources/viewers/shell/ViewerShell';

interface DocxViewerProps {
  source: Source;
}

/**
 * Renders `.docx` source files via the `docx-preview` library.
 *
 * Lazy-imports the renderer so its ~350 KB cost only lands when a user
 * actually opens a .docx source. Output is a styled HTML representation
 * inside a scrollable container - close to Word's print layout for most
 * everyday documents (text, headings, tables, basic images, lists).
 */
export function DocxViewer({ source }: DocxViewerProps) {
  const { buffer, isLoading, error } = useSourceArrayBuffer(source.id);
  const containerRef = useRef<HTMLDivElement>(null);
  const renderErrorRef = useRef<string | null>(null);

  useEffect(() => {
    if (!buffer || !containerRef.current) return;
    const target = containerRef.current;
    target.innerHTML = '';
    renderErrorRef.current = null;

    let cancelled = false;
    (async () => {
      try {
        const { renderAsync } = await import('docx-preview');
        if (cancelled) return;
        await renderAsync(buffer, target, undefined, {
          // Render real, paginated pages (Word/Google-Docs style) instead of
          // one collapsed block, so each page reads like an actual document.
          inWrapper: true,
          experimental: true,
          renderHeaders: true,
          renderFooters: true,
        });
      } catch (err: unknown) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'Render failed';
        renderErrorRef.current = msg;
        // Build the notice with textContent (not innerHTML) so a hostile
        // error string can't inject markup. ViewerError lives in a sibling
        // render path, so this is the simplest path without lifting state.
        target.innerHTML = '';
        const notice = document.createElement('div');
        notice.className = 'p-6 text-sm text-red-600 dark:text-red-400';
        notice.textContent = `Failed to render .docx: ${msg}`;
        target.appendChild(notice);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [buffer]);

  if (isLoading) return <ViewerLoading />;
  if (error)
    return <ViewerError message="Couldn't load file" detail={error.message} />;

  return (
    <ViewerShell
      sourceId={source.id}
      icon={FileText}
      label='Document'
      tone='reference'
    >
      <div
        ref={containerRef}
        className={[
          'h-full w-full overflow-auto',
          // Neutralize docx-preview's default gray wrapper so our backdrop
          // shows; center the page stack with comfortable breathing room.
          '[&_.docx-wrapper]:!gap-6 [&_.docx-wrapper]:!bg-transparent [&_.docx-wrapper]:!p-6',
          // Style each page as a clean document card matching the other viewers.
          '[&_.docx]:!rounded-xl [&_.docx]:!border-0 [&_.docx]:!shadow-lg [&_.docx]:!ring-1 [&_.docx]:!ring-border/70',
        ].join(' ')}
      />
    </ViewerShell>
  );
}
